import React, { useEffect, useRef, useState } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import DescriptionOutlined from '@material-ui/icons/DescriptionOutlined';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import InsertDriveFileOutlined from '@material-ui/icons/InsertDriveFileOutlined';
import { saveAs } from 'file-saver';
import { detectPreviewKind, getAttachmentBlob } from '../services/attachments';

const StyledButton = withStyles({
    root: {
        fontFamily: 'Assistant',
        fontSize: '14px',
        fontWeight: '600',
        textAlign: 'center',
        borderRadius: '19px',
        color: 'white',
        backgroundColor: '#1274e4',
        textTransform: 'none',
        padding: '6px 24px',
        '&:hover': {
            backgroundColor: '#1060BB'
        }
    }
})(Button);

const WhiteStyledButton = withStyles({
    root: {
        fontFamily: 'Assistant',
        fontSize: '14px',
        fontWeight: '600',
        textAlign: 'center',
        borderRadius: '19px',
        color: '#1274e4',
        backgroundColor: 'white',
        border: '1px solid #1274e4',
        textTransform: 'none',
        padding: '6px 24px',
        '&:hover': {
            backgroundColor: 'rgba(240, 240, 240, 0.8)'
        }
    }
})(Button);

const useStyles = makeStyles({
    title: {
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    titleText: {
        fontFamily: 'Assistant',
        fontSize: '18px',
        fontWeight: 700,
        color: '#35373D',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        minHeight: '300px',
        backgroundColor: '#FAFAFA'
    },
    centered: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px'
    },
    message: {
        fontFamily: 'Assistant',
        fontSize: '16px',
        color: '#35373D',
        marginTop: '16px',
        textAlign: 'center'
    },
    image: {
        maxWidth: '100%',
        maxHeight: '70vh',
        objectFit: 'contain',
        margin: 'auto'
    },
    pdfFrame: {
        width: '100%',
        height: '70vh',
        border: 'none'
    },
    docxContainer: {
        maxHeight: '70vh',
        overflow: 'auto',
        backgroundColor: '#E8E8E8',
        '& .docx-wrapper': {
            padding: '16px',
            background: 'transparent'
        }
    },
    fallbackIcon: {
        fontSize: '64px',
        color: '#777777'
    },
    errorIcon: {
        fontSize: '64px',
        color: '#DC143C'
    },
    actions: {
        padding: '12px 24px'
    }
});

export default function AttachmentPreviewDialog({ open, onClose, fileId, meta, RTL }) {
    const classes = useStyles();
    const docxContainerRef = useRef(null);
    const objectUrlRef = useRef(null);
    const [state, setState] = useState({ status: 'idle', kind: null, blob: null, objectUrl: null, error: null });
    const [retryCount, setRetryCount] = useState(0);

    const revokeObjectUrl = () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    };

    const strings = {
        loading: RTL ? 'טוען תצוגה מקדימה...' : 'Loading preview…',
        loadError: RTL ? 'לא ניתן לטעון את הקובץ' : 'Could not load the file',
        docFallback: RTL
            ? 'תצוגה מקדימה אינה זמינה עבור קבצי Word מסוג ‎.doc. ניתן להוריד את הקובץ לצפייה.'
            : 'Preview is not available for legacy .doc Word files. Download the file to view it.',
        otherFallback: RTL
            ? 'תצוגה מקדימה אינה זמינה עבור סוג קובץ זה. ניתן להוריד את הקובץ לצפייה.'
            : 'Preview is not available for this file type. Download the file to view it.',
        download: RTL ? 'הורדה' : 'Download',
        close: RTL ? 'סגירה' : 'Close',
        retry: RTL ? 'נסו שוב' : 'Try again',
        defaultName: RTL ? 'קובץ מצורף' : 'Attached file'
    };

    const filename = (meta && meta.filename) || strings.defaultName;

    useEffect(() => {
        // Keep the current content mounted while the dialog is closed so it
        // stays visible through MUI's exit fade; teardown happens in onExited.
        if (!open || !fileId) {
            return undefined;
        }
        let cancelled = false;
        revokeObjectUrl();
        setState({ status: 'loading', kind: null, blob: null, objectUrl: null, error: null });
        getAttachmentBlob(fileId)
            .then(blob => {
                if (cancelled) return;
                const kind = detectPreviewKind({
                    content_type: (meta && meta.content_type) || blob.type || '',
                    filename: meta && meta.filename
                });
                let createdUrl = null;
                if (kind === 'image' || kind === 'pdf') {
                    createdUrl = URL.createObjectURL(blob);
                    objectUrlRef.current = createdUrl;
                }
                setState({ status: 'ready', kind, blob, objectUrl: createdUrl, error: null });
            })
            .catch(error => {
                if (!cancelled) {
                    setState({ status: 'error', kind: null, blob: null, objectUrl: null, error });
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fileId, retryCount]);

    // Revoke the last object URL if the component unmounts mid-preview.
    useEffect(() => revokeObjectUrl, []);

    const handleExited = () => {
        revokeObjectUrl();
        setState({ status: 'idle', kind: null, blob: null, objectUrl: null, error: null });
    };

    useEffect(() => {
        if (state.status !== 'ready' || state.kind !== 'docx' || !docxContainerRef.current) {
            return undefined;
        }
        let cancelled = false;
        const container = docxContainerRef.current;
        container.innerHTML = '';
        import('docx-preview')
            .then(({ renderAsync }) => {
                if (cancelled) return null;
                return renderAsync(state.blob, container, undefined, { inWrapper: true, ignoreLastRenderedPageBreak: true });
            })
            .catch(error => {
                if (!cancelled) {
                    setState(prev => ({ ...prev, status: 'error', error }));
                }
            });
        return () => {
            cancelled = true;
            container.innerHTML = '';
        };
    }, [state]);

    const handleDownload = () => {
        if (state.blob) {
            saveAs(state.blob, meta && meta.filename ? meta.filename : 'attachment');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth TransitionProps={{ onExited: handleExited }}>
            <div dir={RTL ? 'rtl' : 'ltr'}>
                <DialogTitle disableTypography classes={{ root: classes.title }}>
                    <Typography component="span" className={classes.titleText}>{filename}</Typography>
                    <IconButton size="small" onClick={onClose} aria-label={strings.close}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers className={classes.content}>
                    {state.status === 'loading' &&
                        <div className={classes.centered}>
                            <CircularProgress />
                            <Typography className={classes.message}>{strings.loading}</Typography>
                        </div>
                    }
                    {state.status === 'error' &&
                        <div className={classes.centered}>
                            <ErrorOutline className={classes.errorIcon} />
                            <Typography className={classes.message}>{strings.loadError}</Typography>
                        </div>
                    }
                    {state.status === 'ready' && state.kind === 'image' &&
                        <img src={state.objectUrl} alt={filename} className={classes.image} />
                    }
                    {state.status === 'ready' && state.kind === 'pdf' &&
                        <iframe src={state.objectUrl} title={filename} className={classes.pdfFrame} />
                    }
                    {state.status === 'ready' && state.kind === 'docx' &&
                        <div dir="ltr" ref={docxContainerRef} className={classes.docxContainer} />
                    }
                    {state.status === 'ready' && state.kind === 'doc' &&
                        <div className={classes.centered}>
                            <DescriptionOutlined className={classes.fallbackIcon} />
                            <Typography className={classes.message}>{strings.docFallback}</Typography>
                        </div>
                    }
                    {state.status === 'ready' && state.kind === 'other' &&
                        <div className={classes.centered}>
                            <InsertDriveFileOutlined className={classes.fallbackIcon} />
                            <Typography className={classes.message}>{strings.otherFallback}</Typography>
                        </div>
                    }
                </DialogContent>
                <DialogActions className={classes.actions}>
                    {state.status === 'error' &&
                        <WhiteStyledButton onClick={() => setRetryCount(c => c + 1)}>{strings.retry}</WhiteStyledButton>
                    }
                    {state.status === 'ready' &&
                        <StyledButton onClick={handleDownload}>{strings.download}</StyledButton>
                    }
                    <WhiteStyledButton onClick={onClose}>{strings.close}</WhiteStyledButton>
                </DialogActions>
            </div>
        </Dialog>
    );
}
